export interface MembershipYear {
  start: Date
  end: Date
}

export interface MembershipYearContext {
  start: Date
  end: Date
  reviewYearStart: Date
  retainVariant: 'current-year' | 'next-year'
}

/**
 * Start of the 12-month "join-month cycle" that ENDS at `endDate`. Per IBS rules every
 * membership-year end and tier-review date lands on the last day of the member's join month.
 */
function getJoinMonthCycleStart(endDate: string): Date {
  const end = new Date(endDate)
  return new Date(Date.UTC(end.getUTCFullYear() - 1, end.getUTCMonth() + 1, 1))
}

/**
 * Pure derivation of membership-year boundaries + retain-variant for a member account.
 * All three inputs come straight from the loyalty API account payload.
 *
 * `start` is the member's actual first day in the current membership year:
 * - Year 1: the join date (`createdAt`)
 * - Year 2+: 1st of the month after join month
 *
 * `retainVariant`:
 * - `'current-year'`: tier review at end of current membership year (`statusReviewDate === membershipYearEndDate`).
 * - `'next-year'`: member upgraded mid-year so the tier review is in the *following* membership year.
 *
 * Returns `undefined` when any input is missing — callers should use this to gate
 * UI that depends on the membership-year context.
 */
export function getMembershipYearContext(
  statusReviewDate: string | undefined,
  membershipYearEndDate: string | undefined,
  createdAt: string | undefined,
): MembershipYearContext | undefined {
  if (!statusReviewDate || !membershipYearEndDate || !createdAt) return undefined
  const end = new Date(membershipYearEndDate)
  const enrolment = new Date(createdAt)

  // Determine if its first membership year
  const isYear1 =
    end.getUTCFullYear() === enrolment.getUTCFullYear() + 1 &&
    end.getUTCMonth() === enrolment.getUTCMonth()
  const start = isYear1 ? enrolment : getJoinMonthCycleStart(membershipYearEndDate)
  const retainVariant: 'current-year' | 'next-year' =
    new Date(statusReviewDate).getTime() > end.getTime() ? 'next-year' : 'current-year'

  // If the tier review date is after the membership year end, then the review year starts the day after the current membership year ends.
  const reviewYearStart = retainVariant === 'current-year' ? start : getJoinMonthCycleStart(statusReviewDate)
  return { start, end, reviewYearStart, retainVariant }
}

export interface TierChange {
  date: string
  /** The tier before the change */
  tierFrom: string | undefined
  /** The tier that was unlocked */
  tierTo: string | undefined
}

/**
 * Returns all tier_change activities within the given membership year, sorted chronologically.
 */
export function getTierChangesInMembershipYear(
  activities: Array<App.LoyaltyAccountActivity>,
  membershipYear: MembershipYear,
): Array<TierChange> {
  return activities
    .filter(activity => {
      if (activity.type !== 'tier_change') return false
      const activityDate = new Date(activity.date)
      return activityDate >= membershipYear.start && activityDate <= membershipYear.end
    })
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .map(activity => ({
      date: activity.date,
      tierFrom: activity.tierFrom,
      tierTo: activity.tierTo,
    }))
}
